import clsx from "clsx";
import React, {
  Fragment,
  useRef,
  useEffect,
  useCallback,
  useState,
  useMemo,
} from "react";
import { NavigationResponse } from "@repo/apis/browse-navigation-v2";
import { Link } from "@repo/next-utils/router";
import * as Accordion from "@repo/ui/accordion";
import * as Auth from "@repo/ui/auth";
import { Button } from "@repo/ui/button";
import * as Flex from "@repo/ui/flex";
import * as Sheet from "@repo/ui/sheet";
import { useMediaQuery } from "@repo/ui/useMediaQuery";
import { VisuallyHidden } from "@repo/ui/visually-hidden";
import { useFloorContext } from "@/components/floor-context/floor-context";
import {
  TOPSHOP_INSTAGRAM_URL,
  TOPMAN_INSTAGRAM_URL,
  TIKTOK_URL,
  PINTEREST_URL,
  CORPORATE_RESPONSIBILITY_URL,
} from "@/constants";
import { CloseIcon } from "@/theme/icons/close";
import {
  TYPOGRAPHY_LABEL3,
  TYPOGRAPHY_LABEL3_BLACK,
} from "@/theme/topshop/typography/typography-constants";
import { Logo } from "../logo/logo";
import * as Viewport from "../viewport";
import {
  extractNavigationItems,
  type GroupedNavigationItems,
} from "./extract-nav-items";
import classes from "./nav-drawer.module.css";

type DrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  initialNavigation: NavigationResponse | null;
  country?: string;
};

type TabValue = "topshop" | "topman";

const INSTAGRAM_URLS = {
  topshop: TOPSHOP_INSTAGRAM_URL,
  topman: TOPMAN_INSTAGRAM_URL,
} as const;

export const NavDrawer = ({
  isOpen,
  onClose,
  initialNavigation,
}: DrawerProps) => {
  const [isInitialRender, setIsInitialRender] = useState(true);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );
  const { floor } = useFloorContext();
  const [activeTab, setActiveTab] = useState<TabValue>(
    floor === "topman" ? "topman" : "topshop",
  );

  useEffect(() => {
    setActiveTab(floor === "topman" ? "topman" : "topshop");
  }, [floor]);

  const instagramUrl = floor ? INSTAGRAM_URLS[floor] : TOPSHOP_INSTAGRAM_URL;

  useEffect(() => {
    if (isInitialRender && isOpen) {
      setIsInitialRender(false);
    }
  }, [isOpen, isInitialRender]);

  useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleTabChange = useCallback((value: TabValue) => {
    setActiveTab(value);
  }, []);

  const categories = useMemo(() => {
    const defaultNavigation = {
      navigation: [],
      brands: [],
      footer: [],
      NavigationSettings: {
        MaxWebLargeColumnSpan: 0,
      },
    };

    const nav = initialNavigation ?? defaultNavigation;

    return {
      topshop: extractNavigationItems(nav.navigation, "topshop"),
      topman: extractNavigationItems(nav.navigation, "topman"),
    };
  }, [initialNavigation]);

  const hasNavigationData =
    categories.topshop.length > 0 || categories.topman.length > 0;

  const renderNavigationGroups = (groups: GroupedNavigationItems[]) => {
    return groups?.map((group, groupIndex) => (
      <Fragment key={group.groupTitle}>
        <Flex.Container column gap="md" asChild>
          <ul>
            {group.items.map((category, index) => {
              const isSale = category.style.mobileStyleType === "sale";

              return (
                <li
                  key={index}
                  className={clsx(
                    classes.link,
                    isSale ? TYPOGRAPHY_LABEL3_BLACK : TYPOGRAPHY_LABEL3,
                    isSale && classes.saleLink,
                  )}
                >
                  <Link href={category.url || "#"} onClick={handleClose}>
                    {category.title}
                  </Link>
                </li>
              );
            })}
          </ul>
        </Flex.Container>
        {groupIndex < groups.length - 1 && <hr className={classes.divider} />}
      </Fragment>
    ));
  };

  const renderSiteMapAccordion = () => (
    <Accordion.Root className={classes.sitemapAccordion} variant="dark">
      <Accordion.Item value="brand">
        <Accordion.Header>
          <Accordion.Trigger
            className={`${TYPOGRAPHY_LABEL3} ${classes.accordionTrigger}`}
          >
            THE BRAND
            <Accordion.TriggerIcon />
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Content>
          <Flex.Container column gap="xs">
            <Link
              href="/about"
              className={TYPOGRAPHY_LABEL3}
              onClick={handleClose}
            >
              ABOUT
            </Link>
            <Link
              href="/press"
              className={TYPOGRAPHY_LABEL3}
              onClick={handleClose}
            >
              PRESS
            </Link>
          </Flex.Container>
        </Accordion.Content>
      </Accordion.Item>

      <Accordion.Item value="follow-us">
        <Accordion.Header>
          <Accordion.Trigger
            className={`${TYPOGRAPHY_LABEL3} ${classes.accordionTrigger}`}
          >
            FOLLOW US
            <Accordion.TriggerIcon />
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Content className={classes.accordionContentLinks}>
          <Flex.Container
            column
            gap="xs"
            className={classes.accordionLinksFlex}
          >
            <a
              href={instagramUrl}
              className={TYPOGRAPHY_LABEL3}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow us on Instagram (opens in a new tab)"
            >
              INSTAGRAM
            </a>
            <a
              href={TIKTOK_URL}
              className={TYPOGRAPHY_LABEL3}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow us on TikTok (opens in a new tab)"
            >
              TIKTOK
            </a>
            <a
              href={PINTEREST_URL}
              className={TYPOGRAPHY_LABEL3}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow us on Pinterest (opens in a new tab)"
            >
              PINTEREST
            </a>
          </Flex.Container>
        </Accordion.Content>
      </Accordion.Item>

      <Accordion.Item value="help">
        <Accordion.Header>
          <Accordion.Trigger
            className={`${TYPOGRAPHY_LABEL3} ${classes.accordionTrigger}`}
          >
            HELP
            <Accordion.TriggerIcon />
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Content className={classes.accordionContentLinks}>
          <Flex.Container
            column
            gap="xs"
            className={classes.accordionLinksFlex}
          >
            <Link
              href="/delivery-and-returns"
              className={TYPOGRAPHY_LABEL3}
              onClick={handleClose}
            >
              DELIVERY & RETURNS
            </Link>
            <Link
              href="/customer-care"
              className={TYPOGRAPHY_LABEL3}
              onClick={handleClose}
            >
              CUSTOMER CARE
            </Link>
          </Flex.Container>
        </Accordion.Content>
      </Accordion.Item>
      <Accordion.Item value="policies">
        <Accordion.Header>
          <Accordion.Trigger
            className={`${TYPOGRAPHY_LABEL3} ${classes.accordionTrigger}`}
          >
            POLICIES
            <Accordion.TriggerIcon />
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Content className={classes.accordionContentLinks}>
          <Flex.Container
            column
            gap="xs"
            className={classes.accordionLinksFlex}
          >
            <Link
              href="/content/data-protection"
              className={TYPOGRAPHY_LABEL3}
              target="_blank"
            >
              DATA PROTECTION
            </Link>
            <Link
              href="/content/compliance"
              className={TYPOGRAPHY_LABEL3}
              target="_blank"
            >
              COMPLIANCE
            </Link>
            <Link
              href="/content/cyber-security"
              className={TYPOGRAPHY_LABEL3}
              target="_blank"
            >
              CYBERSECURITY
            </Link>
            <Link
              href="/content/accessibility"
              className={TYPOGRAPHY_LABEL3}
              target="_blank"
            >
              ACCESSIBILITY
            </Link>
            <a
              href={CORPORATE_RESPONSIBILITY_URL}
              className={TYPOGRAPHY_LABEL3}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Corporate Responsibility (opens in a new tab)"
            >
              CORPORATE RESPONSIBILITY
            </a>
          </Flex.Container>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );

  return (
    <Sheet.Root open={isOpen} onOpenChange={onClose} modal={isOpen}>
      <Sheet.Content
        position="left"
        overlayAnimation={!prefersReducedMotion}
        className={clsx(
          classes.navDrawer,
          isInitialRender && classes.initialRender,
        )}
        forceMount
      >
        <Flex.Container column className={classes.content}>
          <Sheet.Header>
            <Flex.Container
              className={classes.header}
              justify="start"
              align="center"
            >
              <Sheet.Title>
                <VisuallyHidden>Menu</VisuallyHidden>
              </Sheet.Title>
              <Sheet.Description>
                <VisuallyHidden>
                  Navigation menu for categories and my account
                </VisuallyHidden>
              </Sheet.Description>
              <Sheet.Close ref={closeButtonRef} asChild>
                <Button size="icon">
                  <CloseIcon label="Close menu" className={classes.icon} />
                </Button>
              </Sheet.Close>
            </Flex.Container>
          </Sheet.Header>
          <Sheet.Main className={classes.main}>
            {hasNavigationData ? (
              <>
                <div className={classes.tabs}>
                  <Flex.Container gap="lg" style={{ alignItems: "stretch" }}>
                    <button
                      className={clsx(classes.tabsTrigger, {
                        [classes.activeTab || ""]: activeTab === "topshop",
                        [classes.inactiveTab || ""]: activeTab === "topman",
                      })}
                      onClick={() => handleTabChange("topshop")}
                      aria-selected={activeTab === "topshop"}
                      role="tab"
                    >
                      <Logo variant="topshop" className={classes.tabLogo} />
                      <VisuallyHidden>TOPSHOP</VisuallyHidden>
                    </button>
                    <button
                      className={clsx(classes.tabsTrigger, {
                        [classes.activeTab || ""]: activeTab === "topman",
                        [classes.inactiveTab || ""]: activeTab === "topshop",
                      })}
                      onClick={() => handleTabChange("topman")}
                      aria-selected={activeTab === "topman"}
                      role="tab"
                    >
                      <Logo variant="topman" className={classes.tabLogo} />
                      <VisuallyHidden>TOPMAN</VisuallyHidden>
                    </button>
                  </Flex.Container>
                </div>
                <Flex.Container column className={classes.nav} gap="xl">
                  {activeTab === "topshop"
                    ? renderNavigationGroups(categories.topshop)
                    : renderNavigationGroups(categories.topman)}

                  <hr className={classes.divider} />

                  <Flex.Container column gap="sm">
                    <Link
                      className={clsx(classes.link, TYPOGRAPHY_LABEL3)}
                      href="/wishlist"
                    >
                      Wishlist
                    </Link>
                    <Auth.Login
                      className={`${classes.link} ${TYPOGRAPHY_LABEL3_BLACK}`}
                    >
                      LOGIN / SIGN UP
                    </Auth.Login>
                    <Auth.Logout
                      className={`${classes.link} ${TYPOGRAPHY_LABEL3_BLACK}`}
                    >
                      SIGN OUT
                    </Auth.Logout>
                  </Flex.Container>

                  <Viewport.Small>{renderSiteMapAccordion()}</Viewport.Small>
                </Flex.Container>
              </>
            ) : (
              <Flex.Container column gap="sm">
                <div className={TYPOGRAPHY_LABEL3}>
                  <p>Unable to load navigation items</p>
                </div>
                <Flex.Container column gap="sm">
                  <Auth.Login
                    className={`${classes.link} ${TYPOGRAPHY_LABEL3_BLACK}`}
                  >
                    LOGIN / SIGN UP
                  </Auth.Login>
                  <Auth.Logout
                    className={`${classes.link} ${TYPOGRAPHY_LABEL3_BLACK}`}
                  >
                    SIGN OUT
                  </Auth.Logout>
                </Flex.Container>
                <Viewport.Small>{renderSiteMapAccordion()}</Viewport.Small>
              </Flex.Container>
            )}
          </Sheet.Main>
          <div className={classes.footer}>
            <p className={classes.copyright}>
              &copy; {new Date().getFullYear()} Topshop
            </p>
          </div>
        </Flex.Container>
      </Sheet.Content>
    </Sheet.Root>
  );
};
